import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import styles from './ProfilePage.module.css'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'

interface UserProfile {
  id: number
  user: number
  mental_health_concerns: string
  specialist_gender_preference: string
  specialist_language_preference: string
}

export default function ProfilePage() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: ''
  })

  useEffect(() => {
    if (!user) {
      navigate('/login')
      return
    }

    fetchUserProfile()
  }, [user, navigate])

  useEffect(() => {
    if (user) {
      setEditForm({
        first_name: user.first_name || '',
        last_name: user.last_name || ''
      })
    }
  }, [user])

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('access_token')
      if (!token) return

      const response = await fetch(`${API_BASE_URL}/user-profile/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        const data = await response.json()
        setUserProfile(data)
      }
    } catch (error) {
      console.error('Error fetching user profile:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const handleEditProfile = () => {
    setIsEditing(!isEditing)
  }

  const handleSaveProfile = async () => {
    try {
      const token = localStorage.getItem('access_token')
      if (!token) {
        alert('Not authenticated. Please login again.')
        return
      }

      console.log('Saving profile:', editForm)
      console.log('User ID:', user?.id)

      // Update user profile - use /users/me/ endpoint
      const response = await fetch(`${API_BASE_URL}/users/me/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editForm)
      })

      console.log('Response status:', response.status)
      const responseData = await response.json()
      console.log('Response data:', responseData)

      if (response.ok) {
        alert('Profile updated successfully!')
        // Refresh the page to get updated data
        window.location.reload()
      } else {
        alert(`Error updating profile: ${responseData.detail || JSON.stringify(responseData)}`)
      }
    } catch (error) {
      console.error('Error updating profile:', error)
      alert(`Error updating profile: ${error}`)
    }
  }

  const handleCancelEdit = () => {
    // Reset form to current user data
    setEditForm({
      first_name: user?.first_name || '',
      last_name: user?.last_name || ''
    })
    setIsEditing(false)
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const getInitials = () => {
    if (!user || !user.first_name || !user.last_name) return '?'
    const firstName = user.first_name.charAt(0) || ''
    const lastName = user.last_name.charAt(0) || ''
    return `${firstName}${lastName}`.toUpperCase()
  }

  const getUserName = () => {
    if (!user) return 'User'
    const firstName = user.first_name || 'Unknown'
    const lastName = user.last_name || 'User'
    return `${firstName} ${lastName}`
  }

  if (!user && loading) {
    return (
      <div className={styles.profileContainer}>
        <div className={styles.profileWrapper}>
          <p>Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.profileContainer}>
      <div className={styles.profileWrapper}>
        {/* Header */}
        <div className={styles.header}>
          <h1 className={styles.headerTitle}>My Profile</h1>
          <button className={styles.backButton} onClick={() => navigate('/dashboard')}>
            ← Back to Dashboard
          </button>
        </div>

        {/* Main Profile Card */}
        <div className={styles.profileCard}>
          {/* Avatar and Basic Info */}
          <div className={styles.avatarSection}>
            <div className={styles.avatar}>{getInitials()}</div>
            <div className={styles.avatarInfo}>
              <h2 className={styles.userName}>
                {getUserName()}
              </h2>
              <p className={styles.userEmail}>{user?.email || 'email@example.com'}</p>
              <div className={styles.userStatus}>
                <div className={styles.statusIndicator}></div>
                {user?.is_active ? 'Active' : 'Inactive'}
              </div>
              <div className={styles.memberBadge}>
                Member since {user?.date_joined ? formatDate(user.date_joined) : 'Unknown'}
              </div>
            </div>
          </div>

          {/* Key Information Grid */}
          <div className={styles.infoGrid}>
            <div className={styles.infoCard}>
              <div className={styles.infoLabel}>Account ID</div>
              <div className={styles.infoValue}>#{user?.id}</div>
            </div>

            <div className={styles.infoCard}>
              <div className={styles.infoLabel}>Account Status</div>
              <div className={styles.infoValue}>
                {user?.is_active ? (
                  <span style={{ color: '#4caf50' }}>✓ Active</span>
                ) : (
                  <span style={{ color: '#f44336' }}>✗ Inactive</span>
                )}
              </div>
            </div>

            <div className={styles.infoCard}>
              <div className={styles.infoLabel}>Member Since</div>
              <div className={styles.infoValue}>
                {user?.date_joined ? formatDate(user.date_joined) : 'N/A'}
              </div>
            </div>
          </div>

          {/* Account Details Section */}
          {userProfile && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Profile Preferences</h3>
              <ul className={styles.detailsList}>
                {userProfile.mental_health_concerns && (
                  <li className={styles.detailsItem}>
                    <span className={styles.detailsLabel}>Mental Health Concerns</span>
                    <span className={styles.detailsValue}>
                      {userProfile.mental_health_concerns}
                    </span>
                  </li>
                )}
                {userProfile.specialist_gender_preference && (
                  <li className={styles.detailsItem}>
                    <span className={styles.detailsLabel}>Specialist Gender Preference</span>
                    <span className={styles.detailsValue}>
                      {userProfile.specialist_gender_preference}
                    </span>
                  </li>
                )}
                {userProfile.specialist_language_preference && (
                  <li className={styles.detailsItem}>
                    <span className={styles.detailsLabel}>Language Preference</span>
                    <span className={styles.detailsValue}>
                      {userProfile.specialist_language_preference}
                    </span>
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Account Information Section */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Account Information</h3>
            <ul className={styles.detailsList}>
              <li className={styles.detailsItem}>
                <span className={styles.detailsLabel}>Email Address</span>
                <span className={styles.detailsValue}>{user?.email || 'N/A'}</span>
              </li>
              <li className={styles.detailsItem}>
                <span className={styles.detailsLabel}>First Name</span>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.first_name}
                    onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                    className={styles.editInput}
                  />
                ) : (
                  <span className={styles.detailsValue}>{user?.first_name || 'Not provided'}</span>
                )}
              </li>
              <li className={styles.detailsItem}>
                <span className={styles.detailsLabel}>Last Name</span>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.last_name}
                    onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                    className={styles.editInput}
                  />
                ) : (
                  <span className={styles.detailsValue}>{user?.last_name || 'Not provided'}</span>
                )}
              </li>
              <li className={styles.detailsItem}>
                <span className={styles.detailsLabel}>Account Created</span>
                <span className={styles.detailsValue}>{user?.date_joined ? formatDate(user.date_joined) : 'N/A'}</span>
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className={styles.actionButtons}>
            {isEditing ? (
              <>
                <button className={styles.saveButton} onClick={handleSaveProfile}>
                  ✓ Save Changes
                </button>
                <button className={styles.cancelButton} onClick={handleCancelEdit}>
                  ✕ Cancel
                </button>
              </>
            ) : (
              <>
                <button className={styles.editButton} onClick={handleEditProfile}>
                  ✎ Edit Profile
                </button>
                <button className={styles.logoutButton} onClick={handleLogout}>
                  🚪 Logout
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
